import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useArc } from '../context/ArcContext';
import { BASELINE_RITUALS } from '../mock/mock';
import { ArrowRight, Check, Landmark, PiggyBank, Sparkles, Zap } from 'lucide-react';

const EXPENSE_FIELDS = [
  { key: 'rent', label: 'Housing' },
  { key: 'food', label: 'Food' },
  { key: 'transport', label: 'Transport' },
  { key: 'utilities', label: 'Utilities' },
  { key: 'insurance', label: 'Insurance' },
  { key: 'subscriptions', label: 'Subscriptions' },
  { key: 'other', label: 'Other' },
];

const emptyFinance = {
  expenses: Object.fromEntries(EXPENSE_FIELDS.map(field => [field.key, ''])),
  primaryIncome: '',
  secondaryIncome: '',
};

function moneyValue(value) {
  const next = Number(value || 0);
  return Number.isFinite(next) && next > 0 ? next : 0;
}

export default function Baseline() {
  const { setBaseline, state, engine, arc } = useArc();
  const navigate = useNavigate();
  const [vals, setVals] = useState(() => (
    state.baselineRituals || Object.fromEntries(BASELINE_RITUALS.map(r => [r.id, false]))
  ));
  const [finance, setFinance] = useState(() => ({
    expenses: { ...emptyFinance.expenses, ...Object.fromEntries(EXPENSE_FIELDS.map(field => [field.key, state.financials?.expenses?.[field.key] || ''])) },
    primaryIncome: state.financials?.primaryIncome || '',
    secondaryIncome: state.financials?.secondaryIncome || '',
  }));

  const yes = Object.values(vals).filter(Boolean).length;
  const projected = 60 + yes * 8;
  const foundation = useMemo(
    () => Object.values(finance.expenses).reduce((sum, value) => sum + moneyValue(value), 0),
    [finance.expenses]
  );
  const income = moneyValue(finance.primaryIncome) + moneyValue(finance.secondaryIncome);
  const financeReady = foundation > 0 && income > 0;

  const updateExpense = (key, value) => {
    setFinance(prev => ({ ...prev, expenses: { ...prev.expenses, [key]: value } }));
  };

  const lock = () => {
    if (!financeReady) return;
    setBaseline(vals, finance);
    navigate('/app');
  };

  return (
    <div className="relative min-h-screen font-body bg-[#050403] text-white overflow-hidden">
      <div className="myarc-bg" />
      <div className="relative max-w-[1120px] mx-auto px-5 md:px-10 py-10 md:py-12 fade-up">
        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-6 lg:gap-8 items-start">
          <section className="lg:sticky lg:top-8">
            <div className="chip chip-engine inline-flex"><Sparkles size={12} /> Life OS setup</div>
            <h1 className="mt-5 font-head text-[36px] md:text-[52px] leading-[1.05]" style={{ color: 'var(--el)' }}>
              Lock your starting point.
            </h1>
            <p className="mt-4 text-white/70 max-w-[560px] text-[15px] leading-[1.8]">
              Your Arc is assigned by the Brain Scan. This setup gives the Life OS an honest baseline so the compounding chart starts from where you are today.
            </p>

            <div className="mt-6 panel-strong p-5 rounded-2xl">
              <div className="text-[10px] font-data tracking-[0.22em] uppercase text-white/40">Your Arc</div>
              <div className="mt-2 font-head text-[28px]" style={{ color: 'var(--el)' }}>{arc?.key || state.arcKey || 'Locked'}</div>
              <div className="mt-1 text-[13px] text-white/55">{engine?.name || state.engineKey || 'Brain Scan'} baseline</div>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-white/8 bg-white/[0.02] p-4">
                  <div className="eyebrow">Protocol baseline</div>
                  <div className="mt-1 font-head text-[30px]" style={{ color: 'var(--el)' }}>{projected}</div>
                </div>
                <div className="rounded-xl border border-white/8 bg-white/[0.02] p-4">
                  <div className="eyebrow">Rituals active</div>
                  <div className="mt-1 font-head text-[30px] text-white">{yes}/{BASELINE_RITUALS.length}</div>
                </div>
              </div>
            </div>
          </section>

          <section className="space-y-5">
            <div className="panel-strong p-5 md:p-6 rounded-2xl">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'color-mix(in srgb, var(--el) 16%, transparent)', border: '1px solid color-mix(in srgb, var(--el) 40%, transparent)' }}>
                  <Zap size={16} style={{ color: 'var(--el)' }} />
                </div>
                <div>
                  <div className="rail-left font-head text-[16px] text-white/95">Required: protocol baseline</div>
                  <p className="text-[13px] text-white/55 mt-1 leading-[1.65]">
                    Tick what already describes you. Accuracy matters more than optimism.
                  </p>
                </div>
              </div>

              <div className="mt-5 grid md:grid-cols-2 gap-2.5">
                {BASELINE_RITUALS.map(r => {
                  const on = vals[r.id];
                  return (
                    <button
                      key={r.id}
                      onClick={() => setVals(v => ({ ...v, [r.id]: !v[r.id] }))}
                      className="text-left px-4 py-4 rounded-xl border transition-all duration-200"
                      style={{
                        background: on ? 'color-mix(in srgb, var(--el) 14%, transparent)' : 'rgba(255,255,255,0.025)',
                        borderColor: on ? 'color-mix(in srgb, var(--el) 65%, transparent)' : 'rgba(255,255,255,0.07)',
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <span
                          className="h-5 w-5 rounded-full flex items-center justify-center transition-all shrink-0"
                          style={{
                            background: on ? 'var(--el)' : 'rgba(255,255,255,0.04)',
                            border: `1px solid ${on ? 'var(--el)' : 'rgba(255,255,255,0.18)'}`,
                          }}
                        >
                          {on && <Check size={12} className="text-[#050403]" strokeWidth={3} />}
                        </span>
                        <span className="text-[14px] leading-[1.45]" style={{ color: on ? '#fff' : 'rgba(255,255,255,0.78)' }}>{r.label}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="panel p-5 md:p-6 rounded-2xl">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(29,158,117,0.12)', border: '1px solid rgba(29,158,117,0.32)' }}>
                  <PiggyBank size={16} style={{ color: '#1D9E75' }} />
                </div>
                <div>
                  <div className="rail-left font-head text-[16px] text-white/95">Required: financial starting point</div>
                  <p className="text-[13px] text-white/55 mt-1 leading-[1.65]">
                    Add rough monthly income and foundation expenses so Zone Progress starts from a real first check-in.
                  </p>
                </div>
              </div>

              <div className="mt-5 grid md:grid-cols-2 gap-3">
                <label className="text-[12px] text-white/58">
                  Primary monthly income
                  <input
                    type="number"
                    min="0"
                    inputMode="decimal"
                    className="input mt-1"
                    value={finance.primaryIncome}
                    onChange={e => setFinance(prev => ({ ...prev, primaryIncome: e.target.value }))}
                    placeholder="0"
                  />
                </label>
                <label className="text-[12px] text-white/58">
                  Secondary monthly income
                  <input
                    type="number"
                    min="0"
                    inputMode="decimal"
                    className="input mt-1"
                    value={finance.secondaryIncome}
                    onChange={e => setFinance(prev => ({ ...prev, secondaryIncome: e.target.value }))}
                    placeholder="0"
                  />
                </label>
              </div>

              <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {EXPENSE_FIELDS.map(field => (
                  <label key={field.key} className="text-[12px] text-white/58">
                    {field.label}
                    <input
                      type="number"
                      min="0"
                      inputMode="decimal"
                      className="input mt-1"
                      value={finance.expenses[field.key]}
                      onChange={e => updateExpense(field.key, e.target.value)}
                      placeholder="0"
                    />
                  </label>
                ))}
              </div>

              <div className="mt-5 grid sm:grid-cols-3 gap-3">
                <div className="rounded-xl border border-white/8 bg-white/[0.02] p-4">
                  <div className="eyebrow">Foundation</div>
                  <div className="mt-1 font-data text-[20px] text-white">${foundation.toLocaleString()}</div>
                </div>
                <div className="rounded-xl border border-white/8 bg-white/[0.02] p-4">
                  <div className="eyebrow">Income</div>
                  <div className="mt-1 font-data text-[20px] text-white">${income.toLocaleString()}</div>
                </div>
                <div className="rounded-xl border border-white/8 bg-white/[0.02] p-4">
                  <div className="eyebrow">First check-in</div>
                  <div className="mt-1 font-data text-[20px]" style={{ color: financeReady ? '#1D9E75' : 'rgba(255,255,255,0.35)' }}>
                    {financeReady ? 'Ready' : 'Needed'}
                  </div>
                </div>
              </div>
              {!financeReady && (
                <div className="mt-4 px-4 py-3 rounded-lg border border-white/6 bg-white/[0.02] text-[12.5px] text-white/50 leading-[1.7]">
                  Add at least one monthly income number and one monthly expense number to lock the baseline.
                </div>
              )}
            </div>

            <div className="panel-strong p-5 rounded-2xl flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(199,164,109,0.13)', border: '1px solid rgba(199,164,109,0.35)' }}>
                  <Landmark size={15} style={{ color: '#C7A46D' }} />
                </div>
                <div>
                  <div className="font-head text-[15px] text-white">This becomes your Life OS origin.</div>
                  <p className="text-[12.5px] text-white/50 mt-1 leading-[1.6]">
                    The dashboard will start from this baseline, then daily proof and check-ins build the curve.
                  </p>
                </div>
              </div>
              <button
                className="btn btn-primary px-6 py-3.5 shrink-0"
                onClick={lock}
                disabled={!financeReady}
                style={{ opacity: financeReady ? 1 : 0.45, cursor: financeReady ? 'pointer' : 'not-allowed' }}
                data-testid="baseline-lock-btn"
              >
                Lock baseline and enter <ArrowRight size={14} />
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
